import { useState, useEffect } from 'react';
import * as api from '../api/api';
import { normalizeItemFields } from '../utils/insights/itemNormalizers';
import { buildPaginationParams, buildFilterParams } from '../utils/insights/paramBuilders';
import { useTranslation } from 'react-i18next';

/**
 * Hook para cargar datos de la tabla de resúmenes
 * @param {Object} dateRange - Rango de fechas
 * @param {string|null} selectedSupervisor - Supervisor seleccionado
 * @param {Array} selectedAgents - Agentes seleccionados
 * @param {Object} pagination - Objeto de paginación { currentPageIndex, pageKeys, setHasMorePages, updatePaginationKeys }
 * @param {Object} preferences - Preferencias de tabla { pageSize }
 * @param {Array|null} selectedJobNames - Job names seleccionados (opcional, para tab Llamadas del detalle)
 * @returns {Object} { tableItems, loading, error, refreshData }
 */
export function useSummaryData(dateRange, selectedSupervisor, selectedAgents, pagination, preferences, selectedJobNames = null) {
  const { t } = useTranslation();
  const [tableItems, setTableItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    currentPageIndex,
    pageKeys,
    setHasMorePages,
    updatePaginationKeys
  } = pagination;

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      const hasSelectedJobs = selectedJobNames && Array.isArray(selectedJobNames) && selectedJobNames.length > 0;
      const hasDateRange = dateRange?.startDate && dateRange?.endDate;

      if (!hasSelectedJobs && (!dateRange || !hasDateRange)) {
        if (isMounted) {
          setTableItems([]);
          setLoading(false);
          setError(null);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        let params;
        if (hasSelectedJobs) {
          params = { selectedJobNames, type: 'audio' };
        } else {
          const paginationParams = buildPaginationParams(
            currentPageIndex,
            pageKeys,
            preferences.pageSize
          );
          const filterParams = buildFilterParams(
            selectedSupervisor,
            selectedAgents,
            dateRange
          );
          params = {
            ...filterParams,
            ...paginationParams
          };
        }
        
        const response = await api.getSummary(params);
        
        if (!isMounted) return;

        if (response && response.items && Array.isArray(response.items)) {
          // Normalizar los datos para manejar campos con mayúsculas
          const normalizedItems = response.items.map(item => normalizeItemFields(item, t));
          setTableItems(normalizedItems);
          
          // Guardar claves de paginación
          // Validar que timestampFrom sea un valor válido (no "0" ni vacío)
          let isValidTimestamp = response.timestampFrom && 
                                 response.timestampFrom !== "0" && 
                                 response.timestampFrom !== 0 &&
                                 parseInt(response.timestampFrom) > 0;
          
          let timestampToUse = response.timestampFrom;
          
          // WORKAROUND: Si el backend devuelve "0", extraer el timestamp del último item
          if (!isValidTimestamp && response.startKey && normalizedItems.length > 0) {
            const lastItem = normalizedItems[normalizedItems.length - 1];
            const lastTimestamp = lastItem.timestamp;
            
            if (lastTimestamp && lastTimestamp > 0) {
              // Intentar convertir timestamp ISO a número si es necesario
              const timestampNum = typeof lastTimestamp === 'string' 
                ? new Date(lastTimestamp).getTime() 
                : lastTimestamp;
              
              if (timestampNum > 0) {
                timestampToUse = String(timestampNum);
                isValidTimestamp = true;
              }
            }
          }
          
          if (response.startKey && isValidTimestamp) {
            updatePaginationKeys(currentPageIndex, response.startKey, timestampToUse);
          }
          
          // Usar hasMore del backend (no asumir true si hay startKey)
          const backendHasMore = response.hasMore === true;
          setHasMorePages(backendHasMore);
        } else {
          setTableItems([]);
          setHasMorePages(false);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        if (isMounted) {
          setTableItems([]);
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedSupervisor, selectedAgents, currentPageIndex, preferences.pageSize, selectedJobNames]);
  // Nota: pageKeys NO está en las dependencias porque se actualiza dentro del useEffect
  // y causaría un loop infinito. Solo se usa para construir parámetros, no para determinar cuándo hacer la llamada.

  const refreshData = () => {
    // Forzar re-render del useEffect
    setTableItems([...tableItems]);
  };

  return {
    tableItems,
    loading,
    error,
    refreshData
  };
}

